import { useEffect, useRef, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import { page } from "@bestowinc/logger/client";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router";

import type { AnyView, ViewModuleRef } from "@bestowinc/tinkertoy-core";

import {
  FAILURE_EVENT_TAGS,
  isViewLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveFailureError,
} from "../error-telemetry";
import { useModuleLoader } from "../module-loader/module-loader-context";
import { useViewLoader } from "../view-loader";
import { getViewLoadPromise } from "../view-loader/view-read-resource";
import { ViewRenderer } from "../view-renderer";

import { ShellLoaderError } from "./shell-loader-error";
import { useShellLoaderData } from "./use-shell-loader-data";

type ViewLoadState =
  | { status: "pending" }
  | {
      status: "ready";
      view: AnyView;
      /** Canonical id of the view whose bundle produced `view`. */
      viewId: string;
    }
  | { status: "error"; error: unknown };

function getDefaultAnalyticsPageName(viewId: string): string {
  const segments = viewId.split("/").filter(Boolean);
  const finalSegment = segments.at(-1);
  return finalSegment ? `/${finalSegment}` : "/";
}

/**
 * The single component rendered by the catch-all route.
 *
 * Reads the `/load` response via {@link useShellLoaderData}, imports the matched
 * view's bundle from the CDN (the wire response omits the live `View` — its
 * functions can't be serialized), and hands the definition plus every
 * server-resolved field (layout, module versions, resolved interactions, path
 * params) to {@link ViewRenderer}.
 *
 * The base `<div data-testid="tinkertoy-shell">` wrapper renders in every state
 * (including while the view bundle is loading) so the document title is present
 * immediately. A view-bundle load failure is rethrown as a
 * {@link ShellLoaderError} so the route's `errorElement`
 * ({@link import("./ShellErrorBoundary").ShellErrorBoundary}) handles it the
 * same way it handles a server-side loader failure.
 */
export function TinkertoyShell() {
  const data = useShellLoaderData();
  const location = useLocation();
  const moduleLoader = useModuleLoader();
  const viewLoader = useViewLoader();

  const { loadedView } = data;
  const { viewId, viewVersion } = loadedView;
  const navigationKey = location.key;

  const [state, setState] = useState<ViewLoadState>({ status: "pending" });

  // Bundle loading is keyed by view identity only — NOT by navigation. A
  // same-view navigation (query/path-param change) reuses the already-loaded
  // bundle, so the mounted ViewRenderer keeps its state instead of being torn
  // down and rebuilt.
  useEffect(() => {
    let cancelled = false;
    setState({ status: "pending" });

    void getViewLoadPromise(viewLoader, viewId, viewVersion).then(
      (loaded) => {
        if (!cancelled) {
          setState({ status: "ready", view: loaded.definition, viewId });
        }
      },
      (error) => {
        if (!cancelled) {
          setState({ status: "error", error });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [viewLoader, viewId, viewVersion]);

  // Pageview analytics is keyed by navigation instead: one emit per completed
  // navigation, tracked by the last key we emitted for. The ref (rather than
  // effect deps alone) keeps ordinary rerenders and StrictMode's effect replay
  // from double-counting a navigation.
  const lastEmittedNavigationKey = useRef<string | undefined>(undefined);
  const readyPageName =
    state.status === "ready" && state.viewId === viewId
      ? (state.view.meta?.analyticsPageName ??
        getDefaultAnalyticsPageName(state.viewId))
      : undefined;
  useEffect(() => {
    if (readyPageName === undefined) {
      return;
    }
    if (lastEmittedNavigationKey.current === navigationKey) {
      return;
    }
    lastEmittedNavigationKey.current = navigationKey;
    page(readyPageName);
  }, [navigationKey, readyPageName]);

  // Surface a failed view import through the route's error boundary, mirroring
  // how a server-side loader failure reaches it. Thrown in render so React
  // Router's errorElement (a React error boundary for this route) catches it.
  if (state.status === "error") {
    const shellError = new ShellLoaderError({
      message: `Failed to load view "${viewId}" (${viewVersion}).`,
      status: 500,
      code: "views_load_failed",
      errorContext: { errorType: "views_load_failed" },
    });

    if (isViewLoadError(state.error)) {
      const failureError = resolveFailureError(
        state.error,
        shellError.message,
      );
      reportFailureOnce(failureError, {
        tag: FAILURE_EVENT_TAGS.viewBundleLoad,
        errorCode: state.error.code,
        message: failureError.message,
        error: failureError,
        viewId: state.error.viewId,
        viewVersion: state.error.version,
      });
      markFailureReported(shellError);
    }

    throw shellError;
  }

  // The flow's default layout arrives as a WireModuleRef (no `props`); attach an
  // empty record so it satisfies ViewModuleRef. When the view defines its own
  // `layoutOverride`, ViewRenderer re-evaluates that instead and ignores this.
  const layoutModuleRef: ViewModuleRef = {
    ...data.layoutModuleRef,
    props: {},
  };

  return (
    <div data-testid="tinkertoy-shell">
      {/* Flow-level base document title. ViewRenderer overrides it with the
          composed "<view> | <base>" title when the matched view has its own. */}
      <Helmet>
        <title>{data.flowMeta.title}</title>
      </Helmet>
      {state.status === "ready" && (
        <ViewRenderer
          view={state.view}
          viewVersion={viewVersion}
          viewData={data.viewData}
          moduleDataMap={data.moduleDataMap}
          layoutModuleRef={layoutModuleRef}
          resolvedModuleVersions={data.resolvedModuleVersions}
          resolvedInteractions={data.resolvedInteractions}
          currentPathParams={data.pathParams}
          loader={moduleLoader}
        />
      )}
    </div>
  );
}
